//codex-- Give office Admin users a human review queue for technician content; PHP rechecks every privilege.
import React, { useState } from 'react';

const BUTTON = 'min-h-[44px] rounded-lg border px-3 py-2 text-sm font-semibold disabled:opacity-50';

export default function TechWallModeration({ api, appRoot, onChanged }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [suspended, setSuspended] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = async () => {
    const data = await api.moderationQueue();
    setItems(data.items || []); setSuspended(data.suspended_users || []);
  };
  const refresh = async () => {
    if (busy) return;
    setOpen(true); setBusy(true); setError('');
    try { await load(); } catch (failure) { setError(failure.response?.data?.error || failure.message || 'Unable to load moderation.'); }
    finally { setBusy(false); }
  };
  const act = async (label, action) => {
    if (busy || !window.confirm(`${label}? This affects Tech Wall only.`)) return;
    setBusy(true); setError('');
    try { await action(); await load(); await onChanged(); }
    catch (failure) { setError(failure.response?.data?.error || failure.message || 'Unable to save this decision. Refresh the queue.'); }
    finally { setBusy(false); }
  };
  return (
    <section aria-label="Tech Wall moderation" className="mb-5 rounded-2xl border border-indigo-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-semibold text-slate-900">Moderation</h2>
        <button type="button" disabled={busy} onClick={refresh} className={`${BUTTON} text-indigo-700`}>{busy ? 'Loading…' : open ? 'Refresh moderation queue' : 'Open moderation queue'}</button>
      </div>
      {open && <>
        <p className="my-3 text-sm text-slate-600">Review text and attachments before approving. Respond to reports promptly. The oldest 100 pending items are shown; refresh after decisions to see the next items.</p>
        {error && <p role="alert" className="my-3 text-sm text-red-700">{error}</p>}
        {!busy && !items.length && <p className="my-3 text-sm text-slate-500">No content awaiting review.</p>}
        <div className="space-y-4">{items.map((item) => {
          const attachment = /^tech_[a-f0-9]{32}\.(jpe?g|png|gif|pdf)$/i.test(item.file_name || '') ? `${appRoot}/tech_wall_files/${encodeURIComponent(item.file_name)}` : '';
          return <article key={`${item.content_type}:${item.content_id}`} aria-label={`Review ${item.content_type} by ${item.author}`} className="rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold">{item.author} · {item.content_type}</h3>
            <p className="my-3 whitespace-pre-wrap break-words text-sm">{item.content}</p>
            {attachment && <a href={attachment} target="_blank" rel="noopener noreferrer" className="my-2 inline-block text-sm text-indigo-700 underline">Review attachment</a>}
            {(item.reports || []).map((report, index) => <p key={index} className="my-2 whitespace-pre-wrap break-words text-sm text-red-700">Reported: {report.reason}<br />{report.details}</p>)}
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" disabled={busy} className={`${BUTTON} text-green-800`} onClick={() => act('Approve this content', () => api.reviewContent(item.content_type, item.content_id, 'approved'))}>Approve</button>
              <button type="button" disabled={busy} className={`${BUTTON} text-red-700`} onClick={() => act('Remove this content', () => api.reviewContent(item.content_type, item.content_id, 'removed'))}>Remove</button>
              <button type="button" disabled={busy} className={`${BUTTON} text-red-700`} onClick={() => act("Suspend this author's wall posting", () => api.setWallSuspension(item.employee_id, true))}>Suspend wall posting</button>
            </div>
          </article>;
        })}</div>
        <h3 className="mb-2 mt-5 font-semibold">Posting suspended</h3>
        {!suspended.length && <p className="text-sm text-slate-500">No posting suspensions.</p>}
        {suspended.map((user) => <div key={user.employee_id} className="flex flex-wrap items-center justify-between gap-3 border-t py-3">
          <span>{user.author}</span><button type="button" disabled={busy} className={`${BUTTON} text-indigo-700`} onClick={() => act('Restore wall posting', () => api.setWallSuspension(user.employee_id, false))}>Restore posting</button>
        </div>)}
      </>}
    </section>
  );
}
